// One-time migration of the pre-ENG-559 global localStorage keys into the
// current user's namespace. Before user-id namespacing, all draft/session keys
// were global; on the first login after this ships we move that existing set
// into `{base}-{uid}[-charId]` so the logged-in user keeps their in-flight
// drafts and selection. Guarded by a global marker so it runs exactly once per
// browser — at the upgrade boundary, when only legacy un-namespaced keys exist
// (no other user's namespaced keys can coexist with that single run).

const MIGRATION_MARKER = 'aos-ns-migrated-v1'

// Exact-match keys (never carry a charId suffix).
const STATIC_BASES = ['aos-character-v1', 'convorcher_session_id']

// Keys that may carry a trailing `-{charId}` segment.
//
// FROZEN at the versions that existed *bare* (un-namespaced) at the ENG-559
// boundary — this is the ONLY set of keys this one-time migration can ever
// encounter. Do NOT bump these to track the current storage-key versions: every
// write since ENG-559 goes through getStorageKeys() and is already namespaced
// (`-${uid ?? 'anon'}`), so a bare `events-v6`/`face-v7` key is never written and
// listing one is dead. Bumping the list forward (as v4->v5->v6 / v6->v7 did)
// also DROPS the genuine legacy version, so a returning pre-ENG-559 user's bare
// draft stops being migrated. Add older versions here if an even-older bare key
// must be caught; never rename these to the newest schema version.
const PREFIX_BASES = [
  'aos-character-authoring-v4',
  'aos-blueprint-v6',
  'aos-blueprint-face-v6',
  'aos-blueprint-events-v4',
  'aos-blueprint-events-v3',
]

// Move a value from one key to another. Never clobbers an existing target
// (the namespaced key wins); always removes the source.
function moveKey(ls, from, to) {
  const val = ls.getItem(from)
  if (val === null) return
  if (ls.getItem(to) === null) ls.setItem(to, val)
  ls.removeItem(from)
}

export function migrateLegacyKeys(uid) {
  if (typeof window === 'undefined' || !uid) return
  let ls
  try { ls = window.localStorage } catch { return }
  if (!ls) return
  try {
    if (ls.getItem(MIGRATION_MARKER) === '1') return

    for (const base of STATIC_BASES) {
      moveKey(ls, base, `${base}-${uid}`)
    }

    // Snapshot keys up front — we mutate localStorage during the loop.
    for (const k of Object.keys(ls)) {
      for (const base of PREFIX_BASES) {
        if (k === base || k.startsWith(`${base}-`)) {
          const rest = k.slice(base.length) // '' or '-charId…'
          // Already namespaced for this user → leave as-is.
          if (rest === `-${uid}` || rest.startsWith(`-${uid}-`)) break
          moveKey(ls, k, `${base}-${uid}${rest}`)
          break
        }
      }
    }

    ls.setItem(MIGRATION_MARKER, '1')
  } catch { /* ignore — migration is best-effort */ }
}
